// You can write more code here

/* START OF COMPILED CODE */

import AssessmentConclusionUI from '@/game/prefabs/Assessment/AssessmentConclusionUI';
import GreenleafAssessment from '@/game/prefabs/Assessment/GreenleafAssessment';
import Player from '@/game/prefabs/World/Player';
/* START-USER-IMPORTS */
import Slides from '@/common/data/scripts.json';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import {
  waitForSeconds,
  waitUntil,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import AssessmentIntro from '@/game/scenes/Assessment/AssessmentIntro';
import AssessmentUI from '@/game/scenes/Assessment/AssessmentUI';
import AssessmentWalkthroughPanel from '@/game/scenes/Assessment/AssessmentWalkthroughPanel';
import Login from '@/game/scenes/Login/Login';
import { DEFAULT_CURSOR_PATH } from '../../Utils/GameSpriteAssetsMap';
/* END-USER-IMPORTS */

export default class Assessment extends Phaser.Scene {
  constructor() {
    super('Assessment');

    /* START-USER-CTR-CODE */

    /* END-USER-CTR-CODE */
  }

  preload(): void {
    this.load.pack('minigame-sfx', 'game_assets/audio/sfx/minigame-sfx.json');
    this.load.pack(
      'assessment-assets',
      'game_assets/audio/phaser_game_audio/assessment-assets.json',
    );
    this.load.pack(
      'mentor1-pack',
      'game_assets/animations/mentors/mentor1-pack.json',
    );
  }

  editorCreate(): void {
    // world
    const world = this.add.container(0, 0);

    // tilemap
    const tilemap = this.add.image(0, 0, 'battle_map_forest');
    tilemap.scaleX = 4;
    tilemap.scaleY = 4;
    tilemap.setOrigin(0, 0);
    world.add(tilemap);

    // player
    const player = new Player(this, 336, 288);
    player.scaleX = 4;
    player.scaleY = 4;
    player.visible = true;
    world.add(player);

    // greenleafAsessment
    const greenleafAsessment = new GreenleafAssessment(this, 688, 288);
    world.add(greenleafAsessment);

    // assessmentConclusionUI
    const assessmentConclusionUI = new AssessmentConclusionUI(this, 0, 0);
    this.add.existing(assessmentConclusionUI);
    assessmentConclusionUI.visible = false;

    // curtain
    const curtain = this.add.image(0, 0, '1x1sprite');
    curtain.scaleX = 1600;
    curtain.scaleY = 1024;
    curtain.setOrigin(0, 0);
    curtain.tintTopLeft = 0;
    curtain.tintTopRight = 0;
    curtain.tintBottomLeft = 0;
    curtain.tintBottomRight = 0;

    // player (prefab fields)
    player.interactionEnabled = false;

    this.tilemap = tilemap;
    this.world = world;
    this.assessmentConclusionUI = assessmentConclusionUI;
    this.curtain = curtain;

    this.events.emit('scene-awake');
  }

  private tilemap!: Phaser.GameObjects.Image;
  private world!: Phaser.GameObjects.Container;
  private assessmentConclusionUI!: AssessmentConclusionUI;
  private curtain!: Phaser.GameObjects.Image;

  /* START-USER-CODE */

  // Write your code here

  async create() {
    this.editorCreate();
    PhaserAudioManager.setMusicPlaying(false);
    PhaserAudioManager.setAmbiencePlaying(false);
    this.input.setDefaultCursor(`${DEFAULT_CURSOR_PATH}, auto`);
    this.scaleWorld();
    this.events.on('prerender', this.scaleWorld, this);

    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.ASSESSMENT_WALKTHROUGH_COMPLETED,
      async () => {
        await waitForSeconds(this.time, 1);
        assessmentUI.controller.startAssessment();
      },
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.PLAYER_CONFIRMED_QUIT_MAIN_LEVEL,
      this.handleQuitAssessment,
      this,
    );
    // Wait until the assessment UI is loaded and active.
    await waitUntil(
      this.time,
      () =>
        (this.scene.get('AssessmentUI') as AssessmentUI) &&
        this.scene.isActive('AssessmentUI'),
    );
    const assessmentUI = this.scene.get('AssessmentUI') as AssessmentUI;
    const showTutorial = true;
    if (showTutorial) {
      // Wait until the assessment UI is loaded and active.
      await waitUntil(
        this.time,
        () =>
          (this.scene.get('AssessmentUI') as AssessmentUI) &&
          this.scene.isActive('AssessmentUI'),
      );
      const assessmentIntro = this.scene.get(
        'AssessmentIntro',
      ) as AssessmentIntro;
      if (!assessmentIntro) {
        console.error('AssessmentIntro not found');
        return;
      }
      this.scene.bringToTop('AssessmentIntro');
      this.startIntroduction();
    } else {
      assessmentUI.controller.startAssessment();
    }
    assessmentUI.controller.onAssessmentComplete = () => {
      setTimeout(() => {
        getSceneManager()?.pauseScene('AssessmentUI');
      }, 1000);
    };

    // this.assessmentUI.startAssessment(); // For testing.
  }

  startIntroduction() {
    if (PhaserAudioManager.currentMusic) {
      PhaserAudioManager.currentMusic.stop();
      PhaserAudioManager.currentMusic = undefined;
    }
    const assessmentIntro = this.scene.get(
      'AssessmentIntro',
    ) as AssessmentIntro;
    if (!assessmentIntro) {
      console.error('AssessmentIntro not found');
      return;
    }
    const dialogueData = Slides.assessmentScripts.intro;
    const mentorDialoguePanel = assessmentIntro.mentorDialoguePanel;

    mentorDialoguePanel.init(dialogueData);

    mentorDialoguePanel.onDialogueComplete = () => {
      this.scene.bringToTop('AssessmentUI');
      this.scene.bringToTop('AssessmentWalkthroughPanel');
      this.handleInstructionsComplete();
      this.tweens.add({
        targets: this.curtain,
        alpha: 0,
        duration: 1000,
        ease: 'linear',
      });
    };
    mentorDialoguePanel.overrideMentor('npc_mentor_e_portrait');
    assessmentIntro.reveal();
  }

  scaleWorld() {
    const camWidth = this.cameras.main.displayWidth;
    const camHeight = this.cameras.main.displayHeight;
    const worldWidth = this.tilemap.width * this.tilemap.scaleX;
    const worldHeight = this.tilemap.height * this.tilemap.scaleY;
    const scale = Math.max(camWidth / worldWidth, camHeight / worldHeight);
    this.world.setScale(scale);
  }

  handleInstructionsComplete() {
    const assessmentWalkthrough = getSceneManager()?.getScene(
      'AssessmentWalkthroughPanel',
    );
    if (assessmentWalkthrough) {
      (assessmentWalkthrough as AssessmentWalkthroughPanel).start();
    }
  }

  handleQuitAssessment() {
    getSceneManager()?.launchScene('Login', Login, {
      type: 'gameplay',
      stopActiveScene: true,
      removeSubScenes: true,
      removeActiveScene: true,
    });
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
